(() => {
  "use strict";

  const DEFAULT_TUTORIAL_URL = "https://www.d3tape.com/tutorials/";

  if (typeof window.d3BodyMapInitAll === "function") {
    window.d3BodyMapInitAll();
    return;
  }

  const REGIONS = {
    shoulder: { label: "Shoulder", x: 44, y: 30, items: [["Muscle Support", "Kinesiology", "kinesiology"], ["Joint stabilization", "Rigid", "rigid"]], tutorials: [["Kinesiology shoulder", "https://www.d3tape.com/tutorial/kinesiology-tape-shoulder/"], ["Rigid shoulder", "https://www.d3tape.com/tutorial/hypofix-and-rigid-for-shoulder/"]] },
    fingers: { label: "Fingers / hand", x: 33, y: 42, items: [["Everyday taping", "Athletic", "athletic"], ["Post-injury or full immobilization", "Rigid", "rigid"]], tutorials: [["Rigid thumb", "https://www.d3tape.com/tutorial/strapping-a-thumb-with-rigid/"], ["EAB hand and thumb", "https://www.d3tape.com/tutorial/eab-hand-and-thumb-support-tutorial/"]] },
    wrist: { label: "Wrist", x: 68, y: 32, items: [["Muscle support", "Kinesiology", "kinesiology"], ["Everyday taping", "Athletic", "athletic"], ["Post-injury or full immobilization", "Rigid", "rigid"]], tutorials: [["Cohesive wrist", "https://www.d3tape.com/tutorial/cohesive-wrap-around-the-wrist-tutorial/"]] },
    hamstring: { label: "Hamstring", x: 39, y: 53, items: [["Muscle support", "Kinesiology", "kinesiology"], ["Compression", "Cohesive", "cohesive"]], tutorials: [["Kinesiology hamstring", "https://www.d3tape.com/tutorial/kinesiology-tape-for-hamstring/"]] },
    thigh: { label: "Thigh", x: 57, y: 51, items: [["Compression", "EAB", "eab"]], tutorials: [["General taping application", "https://www.d3tape.com/tutorial/kinesiology-tape-general-application/"]] },
    knee: { label: "Knee", x: 69, y: 54, items: [["Muscle Support", "Kinesiology", "kinesiology"], ["Joint stabilization", "Rigid", "rigid"], ["Compression", "Cohesive", "cohesive"]], tutorials: [["Kinesiology knee", "https://www.d3tape.com/tutorial/kinesiology-tape-knee/"], ["Rigid knee", "https://www.d3tape.com/tutorial/rigid-tape/"]] },
    calf: { label: "Calf", x: 28, y: 64, items: [["Muscle Support", "Kinesiology", "kinesiology"], ["Compression", "Cohesive", "cohesive"]], tutorials: [["Kinesiology calf", "https://www.d3tape.com/tutorial/kinesiology-tape-calf/"], ["Alternative calf method", "https://www.d3tape.com/tutorial/calf/"]] },
    ankle: { label: "Ankle", x: 54, y: 68, items: [["Everyday taping", "Athletic", "athletic"], ["Post-injury or full immobilization", "Rigid", "rigid"], ["Muscle support", "Kinesiology", "kinesiology"]], tutorials: [["ASTX ankle", "https://www.d3tape.com/tutorial/astx-ankle-strapping/"], ["Rigid ankle", "https://www.d3tape.com/tutorial/rigid-ankle-strapping-/"]] }
  };
  const escapeHtml = (value) => String(value).replace(/[&<>"]/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;"
  })[char]);

  const getConfig = (root) => {
    const node = root.querySelector("[data-d3-body-map-config]");
    if (!node) return {};

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn("d3 body map: invalid configuration", error);
      return {};
    }
  };

  const tutorialUrl = (config, regionKey, tutorialKey) => {
    const regional = config.tutorials?.[regionKey];
    return config.tutorialUrl || regional?.[tutorialKey] || config.tutorials?.[tutorialKey] || DEFAULT_TUTORIAL_URL;
  };

  const arrowIcon = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const initBodyMap = (root) => {
    if (root.dataset.d3BodyMapReady === "true") return;
    root.dataset.d3BodyMapReady = "true";

    const config = getConfig(root);
    const visual = root.querySelector("[data-map-visual]");
    const areaNav = root.querySelector("[data-area-nav]");
    const panel = root.querySelector("[data-region-panel]");
    if (!visual || !areaNav || !panel) return;

    const image = document.createElement("img");
    image.className = "d3-body-map__runner";
    image.src = config.image || "assets/runner.png";
    image.alt = config.imageAlt || "Runner showing selectable taping areas";
    image.decoding = "async";
    image.loading = "eager";
    if (config.fallbackImage) {
      image.addEventListener("error", () => {
        if (image.src.endsWith(config.fallbackImage)) return;
        image.src = config.fallbackImage;
      }, { once: true });
    }
    visual.append(image);

    const setRegion = (key, options = {}) => {
      const region = REGIONS[key];
      if (!region) return;

      root.querySelectorAll("[data-region]").forEach((control) => {
        control.setAttribute("aria-pressed", String(control.dataset.region === key));
      });

      const recommendations = region.items.map(([use, product]) => {
        return `
          <li class="d3-body-map__recommendation">
            <span class="d3-body-map__recommendation-copy">
              <span class="d3-body-map__use">${escapeHtml(use)}</span>
              <span class="d3-body-map__divider" aria-hidden="true"> – </span>
              <span class="d3-body-map__product">${escapeHtml(product)}</span>
            </span>
          </li>`;
      }).join("");
      const tutorials = region.tutorials || [];
      const primary = tutorials[0];
      const secondary = tutorials[1];
      const primaryLink = primary
        ? `<a class="d3-body-map__tutorial" href="${escapeHtml(primary[1])}" aria-label="Watch ${escapeHtml(primary[0])} tutorial"><span>Watch ${escapeHtml(primary[0])} tutorial</span>${arrowIcon}</a>`
        : "";
      const secondaryLink = secondary
        ? `<a class="d3-body-map__tutorial-secondary" href="${escapeHtml(secondary[1])}" aria-label="Watch ${escapeHtml(secondary[0])} tutorial">Also: ${escapeHtml(secondary[0])}</a>`
        : "";

      panel.innerHTML = `
        <p class="d3-body-map__panel-label">Recommended for</p>
        <h3>${escapeHtml(region.label)}</h3>
        <ul class="d3-body-map__recommendations">${recommendations}</ul>
        <div class="d3-body-map__tutorial-links">${primaryLink}${secondaryLink}</div>`;

      if (options.scroll && window.matchMedia("(max-width: 820px)").matches) {
        panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    Object.entries(REGIONS).forEach(([key, region]) => {
      const position = config.positions?.[key] || {};
      const x = Number.isFinite(Number(position.x)) ? Number(position.x) : region.x;
      const y = Number.isFinite(Number(position.y)) ? Number(position.y) : region.y;
      const hotspot = document.createElement("button");
      hotspot.type = "button";
      hotspot.className = "d3-body-map__hotspot";
      hotspot.dataset.region = key;
      hotspot.style.setProperty("--hotspot-x", `${x}%`);
      hotspot.style.setProperty("--hotspot-y", `${y}%`);
      hotspot.setAttribute("aria-label", `Show tape recommendations for ${region.label}`);
      hotspot.setAttribute("aria-pressed", "false");
      hotspot.innerHTML = `<span class="d3-body-map__hotspot-name" aria-hidden="true">${escapeHtml(region.label)}</span><span class="d3-body-map__sr-only">${escapeHtml(region.label)}</span>`;
      hotspot.addEventListener("click", () => setRegion(key, { scroll: true }));
      hotspot.addEventListener("mouseenter", () => {
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) setRegion(key);
      });
      hotspot.addEventListener("focus", () => setRegion(key));
      visual.append(hotspot);

      const areaButton = document.createElement("button");
      areaButton.type = "button";
      areaButton.className = "d3-body-map__area-button";
      areaButton.dataset.region = key;
      areaButton.setAttribute("aria-pressed", "false");
      areaButton.textContent = region.label;
      areaButton.addEventListener("click", () => setRegion(key));
      areaNav.append(areaButton);
    });

    setRegion(REGIONS[config.defaultRegion] ? config.defaultRegion : "knee");
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-d3-body-map]").forEach(initBodyMap);
  };

  window.d3BodyMapInitAll = initAll;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
